import React, { useState } from 'react';
import { Button } from "./ui/button";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { servicesData } from '../data/services';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();

  const handleNavigation = (id) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
        else window.location.href = `/#${id}`;
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const getServiceTitle = (service) => {
    return t.services[`${service.translationKey}_title`];
  };

  const residentialServices = servicesData.filter(s => s.category === 'residential');
  const commercialServices = servicesData.filter(s => s.category === 'commercial');

  return (
    <nav className="fixed top-0 z-50 w-full border-b border-neutral-800 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/60">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_71ec9cde-94ef-481f-b20e-e9fde84e406a/artifacts/ao9uzq47_6.png" 
              alt="PAC Logo" 
              className="h-20 w-auto" 
            />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-neutral-400 hover:text-red-500 transition-colors">{t.nav.home}</Link>
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-neutral-400 hover:text-red-500 transition-colors outline-none">
                {t.nav.services} <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-neutral-950 border-neutral-800 text-neutral-200 w-64 max-h-[80vh] overflow-y-auto">
                <DropdownMenuItem 
                  onClick={() => handleNavigation('services')}
                  className="focus:bg-red-900/20 focus:text-red-500 cursor-pointer"
                >
                  View All Services
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuLabel className="text-red-500">{t.services.tab_residential}</DropdownMenuLabel>
                {residentialServices.map((service) => (
                  <DropdownMenuItem key={service.id} asChild>
                    <Link 
                      to={`/services/${service.category}/${service.slug}`}
                      className="cursor-pointer focus:bg-neutral-900 focus:text-white w-full block"
                    >
                      {getServiceTitle(service)}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuLabel className="text-red-500">{t.services.tab_commercial}</DropdownMenuLabel>
                {commercialServices.map((service) => (
                  <DropdownMenuItem key={service.id} asChild>
                    <Link 
                      to={`/services/${service.category}/${service.slug}`}
                      className="cursor-pointer focus:bg-neutral-900 focus:text-white w-full block"
                    >
                      {getServiceTitle(service)}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/about-us" className="text-sm font-medium text-neutral-400 hover:text-red-500 transition-colors">{t.nav.about}</Link>

            <Link to="/gallery" data-testid="nav-gallery-link" className="text-sm font-medium text-neutral-400 hover:text-red-500 transition-colors">{t.nav.gallery}</Link>

            <Link to="/contact-us" className="text-sm font-medium text-neutral-400 hover:text-red-500 transition-colors">{t.nav.contact}</Link>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage}
              className="text-neutral-400 hover:text-white flex items-center gap-2"
            >
              <Globe className="h-4 w-4" />
              {language === 'en' ? 'FR' : 'EN'}
            </Button>

            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleNavigation('quote')}>
              {t.nav.quote}
            </Button>
          </div>
          <div className="md:hidden flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage}
              className="text-neutral-400 hover:text-white"
            >
              {language === 'en' ? 'FR' : 'EN'}
            </Button>
            <Button variant="ghost" size="icon" className="text-white hover:bg-neutral-800" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <div className="md:hidden border-t border-neutral-800 p-4 bg-black max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col space-y-4">
            <Link to="/" className="text-sm font-medium text-neutral-400 hover:text-white" onClick={() => setIsMenuOpen(false)}>{t.nav.home}</Link>
            
            <div className="space-y-2">
              <button 
                onClick={() => handleNavigation('services')}
                className="text-sm font-medium text-neutral-400 hover:text-white text-left w-full"
              >
                {t.nav.services} (All)
              </button>
              <div className="pl-4 border-l border-neutral-800 space-y-2 mt-2">
                <p className="text-xs font-bold text-red-500 uppercase">{t.services.tab_residential}</p>
                {residentialServices.map((service) => (
                  <Link 
                    key={service.id}
                    to={`/services/${service.category}/${service.slug}`}
                    className="block text-sm text-neutral-500 hover:text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {getServiceTitle(service)}
                  </Link>
                ))}
                <p className="text-xs font-bold text-red-500 uppercase mt-4">{t.services.tab_commercial}</p>
                {commercialServices.map((service) => (
                  <Link 
                    key={service.id}
                    to={`/services/${service.category}/${service.slug}`}
                    className="block text-sm text-neutral-500 hover:text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {getServiceTitle(service)}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/about-us" className="text-sm font-medium text-neutral-400 hover:text-white" onClick={() => setIsMenuOpen(false)}>{t.nav.about}</Link>
            <Link to="/gallery" data-testid="mobile-nav-gallery-link" className="text-sm font-medium text-neutral-400 hover:text-white" onClick={() => setIsMenuOpen(false)}>{t.nav.gallery}</Link>
            <Link to="/contact-us" className="text-sm font-medium text-neutral-400 hover:text-white" onClick={() => setIsMenuOpen(false)}>{t.nav.contact}</Link>
            
            <Button className="bg-red-600 w-full text-white" onClick={() => handleNavigation('quote')}>
              {t.nav.quote}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
